import { useState } from "react";
import { Button } from "../ui/button";
import { Plus, HelpCircle } from "lucide-react";
import { type FaqItem, useFaqsManager } from "./useFaqsManager";
import { FaqsList } from "./FaqsList";
import { FaqEditForm } from "./FaqEditForm";
import { Modal } from "../Modal";
import { VersionDiffViewer } from "./VersionDiffViewer";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";

interface AdminFaqsTabProps {
  faqs: FaqItem[];
  onRefresh: () => void;
}

export function AdminFaqsTab({ faqs, onRefresh }: AdminFaqsTabProps) {
  const {
    editingFaq,
    setEditingFaq,
    activeDraft,
    startAdd,
    handleEdit,
    handleSaveDraft,
    handleDiscardDraft,
    handleSave,
    handleDelete,
    handleMove,
  } = useFaqsManager(faqs, onRefresh);

  const [diffTarget, setDiffTarget] = useState<FaqItem | null>(null);
  const [targetDraft, setTargetDraft] = useState<any>(null);

  const handleOpenDiff = async (faq: FaqItem) => {
    setDiffTarget(faq);
    setTargetDraft(null);
    try {
      const res = await adminFetch(`/api/admin/drafts?item_type=faq&item_id=${faq.id}`);
      if (res?.success && res.draft) {
        setTargetDraft(res.draft);
      } else {
        toast.info("No unpublished staged drafts exist for this FAQ item.");
      }
    } catch {
      toast.error("Failed to load staging diff.");
    }
  };

  const fieldsToCompare = [
    { key: "question_en", label: "Question (English)" },
    { key: "question_tr", label: "Question (Turkish)" },
    { key: "answer_en", label: "Answer (English)", isLongText: true },
    { key: "answer_tr", label: "Answer (Turkish)", isLongText: true },
  ];

  const liveItem = faqs.find((f) => f.id === editingFaq?.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2">
            <HelpCircle className="h-6 w-6 text-brand-primary" />
            <span>FAQ Settings</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Create, reorder, and refine bilingual frequently asked questions served dynamically to users.
          </p>
        </div>
        {!editingFaq && (
          <Button
            onClick={startAdd}
            className="bg-brand-primary text-white hover:bg-brand-primary/95 font-semibold shrink-0"
          >
            <Plus className="h-4.5 w-4.5 mr-2" /> Add FAQ Item
          </Button>
        )}
      </div>

      {editingFaq ? (
        <FaqEditForm
          editingFaq={editingFaq}
          setEditingFaq={setEditingFaq}
          activeDraft={activeDraft}
          liveItem={liveItem}
          onSave={handleSave}
          onSaveDraft={handleSaveDraft}
          onDiscardDraft={handleDiscardDraft}
          onCancel={() => setEditingFaq(null)}
        />
      ) : (
        <FaqsList
          faqs={faqs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onMove={handleMove}
          onViewDiff={handleOpenDiff}
        />
      )}

      {/* Difference comparison modal */}
      <Modal
        isOpen={!!diffTarget}
        onClose={() => setDiffTarget(null)}
        title="Compare FAQ Versions"
        closeLabel="Close"
      >
        {diffTarget && (
          <div className="py-2">
            <VersionDiffViewer
              live={diffTarget}
              draft={targetDraft}
              fieldsToCompare={fieldsToCompare}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AdminFaqsTab;
